/**
 * MobileHome — Food Pulse mobile home dashboard.
 *
 * 1. 4-up category tile grid (Products · Stores · Deals · Charities)
 * 2. Rotating promo banner (Special Menus this weekend)
 * 3. Location line ("Portland, OR, United States")
 * 4. Nearby Business horizontal carousel — pulls from
 *    ``GET /api/food-businesses`` limited to 10 nearest.
 *
 * Data comes from the existing FoodPulse backend collections; no new
 * routes required.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import { categoryIllustration } from "@/config/categoryIllustrations";
import MobileLocationBar from "./MobileLocationBar";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

export const CATEGORY_TILES = [
  {
    key: "products",
    to: "/m/products",
    title: "Products &\nFood Items",
    icon: "🛒",
    accent: "#F6D77A",
  },
  {
    key: "stores",
    to: "/m/stores",
    title: "Stores, Farms\n& Vendors",
    icon: "🏪",
    accent: "#F5B27A",
  },
  {
    key: "deals",
    to: "/m/deals",
    title: "Deals & Offers\n& Coupons",
    icon: "🏷️",
    accent: "#F49B9B",
  },
  {
    key: "charities",
    to: "/m/charities",
    title: "Charities &\nVolunteers",
    icon: "🤲",
    accent: "#F0908C",
  },
];

const PROMOS = [
  {
    id: "healthy-menu",
    title: "Special Healthy Menu",
    subtitle: "This weekend only",
    cta: "Order now",
    bg: "linear-gradient(90deg,#4C7818 0%,#7EA946 100%)",
    image: "https://images.pexels.com/photos/30951016/pexels-photo-30951016.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    id: "farm-fresh",
    title: "Farm-fresh produce",
    subtitle: "Up to 40% off perishables",
    cta: "See deals",
    bg: "linear-gradient(90deg,#7A4A1E 0%,#C79A3F 100%)",
    image: "https://images.pexels.com/photos/27592996/pexels-photo-27592996.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

export function CategoryTile({ tile, active = false }) {
  return (
    <Link
      to={tile.to}
      data-testid={`home-tile-${tile.key}`}
      className="flex flex-col items-center justify-start rounded-2xl p-2 shadow-sm border transition-transform active:scale-95"
      style={{
        background: active ? FP_PURPLE : "#fff",
        color: active ? "#fff" : "#1c1c1c",
        borderColor: active ? FP_PURPLE : "#e5e7eb",
        minHeight: 118,
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-1.5 text-xl"
        style={{ background: tile.accent }}
      >
        {tile.icon}
      </div>
      <span className="text-[11px] font-semibold text-center leading-[1.15] whitespace-pre-line break-words hyphens-auto px-0.5">
        {tile.title}
      </span>
    </Link>
  );
}

function PromoBanner() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PROMOS.length), 5000);
    return () => clearInterval(t);
  }, []);
  const p = PROMOS[idx];
  return (
    <div className="mx-4 mt-4 rounded-2xl overflow-hidden shadow-md" data-testid="home-promo-banner">
      <div
        className="relative h-40 flex items-center justify-between p-5"
        style={{ background: p.bg }}
      >
        <div className="text-white max-w-[55%] z-10">
          <div className="text-[11px] uppercase tracking-widest opacity-80">Save 50% off</div>
          <div className="mt-1 text-2xl font-extrabold leading-tight" style={{ fontFamily: "serif" }}>
            {p.title}
          </div>
          <div className="mt-1 text-xs opacity-90">{p.subtitle}</div>
          <button className="mt-3 px-4 py-1.5 rounded-full text-[11px] font-bold" style={{ background: "#F5A623", color: "#fff" }}>
            {p.cta.toUpperCase()}
          </button>
        </div>
        <img
          src={p.image}
          alt={p.title}
          loading="lazy"
          className="absolute right-0 top-0 h-full w-[55%] object-cover opacity-90"
        />
      </div>
      {/* Dots */}
      <div className="flex justify-center gap-1.5 py-2 bg-white">
        {PROMOS.map((_, i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: i === idx ? FP_PURPLE : "rgba(101,48,145,0.25)" }}
          />
        ))}
      </div>
    </div>
  );
}

function NearbyBusinesses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-businesses`, { params: { limit: 10 } });
        setItems(res.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return (
    <section className="mt-6 px-4" data-testid="home-nearby-businesses">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold">Nearby Business</h2>
        <Link to="/food-pulse/businesses" className="text-sm font-semibold" style={{ color: FP_GREEN }}>
          View All
        </Link>
      </div>
      {loading ? (
        <div className="text-sm opacity-60">Loading businesses…</div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
          {items.map((b) => {
            const cover = (b.images || [])[0] || categoryIllustration(b.business_type, b.slug, {
              subType: b.sub_type, cuisines: b.cuisines, name: b.name,
            });
            const miles = b.distance_miles ? `${b.distance_miles.toFixed(2)} miles` : null;
            return (
              <Link
                key={b.slug}
                to={`/food-pulse/business/${b.slug}`}
                className="min-w-[260px] max-w-[260px] snap-start bg-gray-100 rounded-2xl overflow-hidden shadow-sm"
                data-testid={`home-nearby-${b.slug}`}
              >
                <div className="p-3 space-y-2">
                  <span className="inline-block text-xs font-bold rounded-md px-2 py-1 text-white" style={{ background: FP_GREEN }}>
                    {b.name}
                  </span>
                  <div className="text-xs font-semibold rounded-md px-2 py-1 inline-block text-white" style={{ background: FP_GREEN }}>
                    Food · Dining
                  </div>
                  {miles && (
                    <div className="text-xs font-semibold rounded-md px-2 py-1 inline-block text-white" style={{ background: FP_PURPLE }}>
                      {miles}
                    </div>
                  )}
                </div>
                {cover && (
                  <img
                    src={cover}
                    alt={b.name}
                    loading="lazy"
                    className="w-full h-32 object-cover"
                  />
                )}
                {b.address && (
                  <div className="px-3 py-3 bg-white text-xs leading-snug">
                    {b.address}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function MobileHome() {
  return (
    <div data-testid="mobile-home">
      {/* Category tiles */}
      <div
        className="grid grid-cols-4 gap-2 px-3 pt-3 pb-4"
        style={{
          background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)",
        }}
      >
        {CATEGORY_TILES.map((t) => (
          <CategoryTile key={t.key} tile={t} />
        ))}
      </div>

      <PromoBanner />

      <MobileLocationBar />

      <NearbyBusinesses />
    </div>
  );
}
